import {
    getChatUsers,
    getChatMessages as fetchChatMessages,
    createMessage as createChatMessage,
    createChat as createSocialChat,
    deleteMessage as deleteChatMessage,
    updateMessage as updateChatMessage,
    viewMessage as viewChatMessage,
    connectToGlobalChatNotificationsChannel
} from "@/services/SocialChatService"
import { playNotificationSound } from "@/utils/helpers"

const selectUserAndOpenChat = async ({ commit, dispatch }, userData) => {
    commit('setSelectedUser', userData)

    if (!userData?.privateChat) {
        commit('setSelectedChat', null)
        return
    }

    await dispatch('getChatMessages', { chatData: userData.privateChat })
}

export default {
    state() {
        return {
            chats: {},
            chatPages: {},
            selectedChat: null,
            users: [],
            selectedUser: null,
            selectedChatMedia: null,
        }
    },

    getters: {
        getChats: (state) => state.chats,
        getUsers: (state) => state.users,
        getSelectedChat: (state) => state.selectedChat,
        getSelectedUser: (state) => state.selectedUser,
        getSelectedChatMedia: (state) => state.selectedChatMedia,
        getChatMessages: (state) => {
            return state.selectedChat?.id ? state.chats[state.selectedChat.id] || [] : []
        },
        getChatPage: (state) => (chatId) => state.chatPages[chatId] || 1
    },

    mutations: {
        setSelectedChat(state, chat) {
            state.selectedChat = chat || null
        },

        setChatMessages(state, { chatId, messages }) {
            if (chatId && Array.isArray(messages)) {
                state.chats[chatId] = messages
            }
        },

        setUsers(state, users) {
            state.users = Array.isArray(users) ? users : []
        },

        setSelectedUser(state, user) {
            state.selectedUser = user || null
        },

        setSelectedChatMedia(state, mediaUrl) {
            state.selectedChatMedia = mediaUrl || null
        },

        upsertChatUser(state, user) {
            if (!user?.id) return

            const userIndex = state.users.findIndex(u => u.id === user.id)

            if (userIndex === -1) {
                state.users = [user, ...state.users]
                return
            }

            state.users = [
                ...state.users.slice(0, userIndex),
                user,
                ...state.users.slice(userIndex + 1)
            ]
        },

        resetChats(state) {
            state.chats = {}
            state.chatPages = {}
            state.selectedChat = null
            state.selectedUser = null
            state.selectedChatMedia = null
        },

        prependMessagesToChat(state, { chatId, messages }) {
            if (chatId && state.chats[chatId]) {
                state.chats[chatId] = [...messages, ...state.chats[chatId]]
            }
        },

        setChatPage(state, { chatId, page }) {
            state.chatPages[chatId] = page
        },

        addMessageToChat(state, { chatId, message }) {
            if (chatId && state.chats[chatId]) {
                state.chats[chatId] = [...state.chats[chatId], message]
            }
        },

        addChatToUser(state, { userId, chat }) {
            const user = state.users.find(u => u.id === userId);
            if (!user) return

            !Array.isArray(user.chats) && (user.chats = [])
            user.chats.push(chat);
        },

        setUserPrivateChat(state, { userId, chat }) {
            const user = state.users.find(u => u.id === userId);
            if (!user) return

            user.privateChat = {
                id: chat.id,
                type: chat.type,
                status: chat.status || 'open',
                created_at: chat.created_at,
                updated_at: chat.updated_at,
                lastMessage: null
            };
        },

        removeMessageFromChat(state, { chatId, messageId }) {
            if (chatId && state.chats[chatId]) {
                state.chats[chatId] = state.chats[chatId].filter(msg => msg.id !== messageId)
            }
        },

        updateMessageInChat(state, { chatId, messageId, updatedMessage }) {
            if (chatId && state.chats[chatId]) {
                const messageIndex = state.chats[chatId].findIndex(msg => msg.id === messageId)
                if (messageIndex !== -1) {
                    state.chats[chatId] = [
                        ...state.chats[chatId].slice(0, messageIndex),
                        updatedMessage,
                        ...state.chats[chatId].slice(messageIndex + 1)
                    ]
                }
            }
        },

        updateLastMessageIfMatch(state, { chatId, message }) {
            const user = state.users.find(u => u.privateChat?.id === chatId)
            if (!user?.privateChat?.lastMessage) return

            if (user.privateChat.lastMessage.id === message.id) {
                user.privateChat.lastMessage = message
            }
        },

        updateLastMessageAfterDelete(state, { chatId, deletedMessageId }) {
            const user = state.users.find(u => u.privateChat?.id === chatId)
            if (!user?.privateChat?.lastMessage) return

            if (user.privateChat.lastMessage.id !== deletedMessageId) return

            const messages = state.chats[chatId]
            if (messages && messages.length > 0) {
                user.privateChat.lastMessage = messages[messages.length - 1]
                return
            }

            user.privateChat.lastMessage = null
        },

        moveUserToTopAndUpdateLastMessage(state, { chatId, message }) {
            const userIndex = state.users.findIndex(u => u.privateChat?.id === chatId);
            if (userIndex === -1) return;

            const user = state.users[userIndex];
            user.privateChat.lastMessage = message;

            if (userIndex > 0) {
                state.users.splice(userIndex, 1);
                state.users.unshift(user);
            }
        },

        markLastMessageAsViewed(state, { chatId, viewer }) {
            const user = state.users.find(u => u.privateChat?.id === chatId)
            if (!user?.privateChat?.lastMessage) return

            const lastMessage = user.privateChat.lastMessage
            if (!Array.isArray(lastMessage.last_viewed_by)) {
                lastMessage.last_viewed_by = []
            }

            const alreadyViewed = lastMessage.last_viewed_by.some(v => v.id === viewer.id)
            if (!alreadyViewed) {
                lastMessage.last_viewed_by.push(viewer)
            }
        },

        setUserPrivateChatFromEvent(state, { senderId, message }) {
            const user = state.users.find(u => u.id === senderId)
            if (!user || user.privateChat) return

            user.privateChat = {
                id: message.chat_id,
                type: 'private',
                status: 'open',
                created_at: message.created_at,
                updated_at: message.updated_at,
                lastMessage: message,
                user: message.sender
            }
        },
    },

    actions: {
        setSelectedChat({ commit }, payload) {
            commit('setSelectedChat', payload)
        },

        setSelectedUser({ commit }, payload) {
            commit('setSelectedUser', payload)
        },

        setSelectedChatMedia({ commit }, payload) {
            commit('setSelectedChatMedia', payload)
        },

        resetChats({ commit }) {
            commit('resetChats')
        },

        async getChatMessages({ commit, dispatch, getters }, payload) {
            try {
                commit('setSelectedChat', payload.chatData)
                dispatch('skeleton/setLoadingState', { key: 'socialChat_loadingMessages', value: true })

                const response = await fetchChatMessages({ chatId: payload.chatData.id, page: 1, perPage: 20 })
                commit('setChatMessages', {
                    chatId: payload.chatData.id,
                    messages: response.data.reverse()
                })
                commit('setChatPage', { chatId: payload.chatData.id, page: 1 })

                dispatch('viewLastChatMessage', { chatId: payload.chatData.id })

                return response.data.length
            } catch (error) {
                dispatch('toastfy/activeErrorNotification', { text: error })
            } finally {
                dispatch('skeleton/setLoadingState', { key: 'socialChat_loadingMessages', value: false })
            }
        },

        async loadMoreMessages({ commit, state, dispatch }, { chatId }) {
            try {
                const currentPage = state.chatPages[chatId] || 1
                const nextPage = currentPage + 1

                dispatch('skeleton/setLoadingState', { key: 'socialChat_loadingMoreMessages', value: true })

                const response = await fetchChatMessages({ chatId, page: nextPage, perPage: 20 })

                if (response.data.length > 0) {
                    commit('prependMessagesToChat', { chatId, messages: response.data.reverse() })
                    commit('setChatPage', { chatId, page: nextPage })
                }

                return response.data.length
            } catch (error) {
                dispatch('toastfy/activeErrorNotification', { text: error })
            } finally {
                dispatch('skeleton/setLoadingState', { key: 'socialChat_loadingMoreMessages', value: false })
            }
        },

        async getChatUsers({ commit, dispatch }, payload) {
            try {
                dispatch('skeleton/setLoadingState', { key: 'socialChat_loadingUsers', value: true })
                const response = await getChatUsers(payload)
                commit('setUsers', response.data)
                return response
            } catch (error) {
                dispatch('toastfy/activeErrorNotification', { text: error })
            } finally {
                dispatch('skeleton/setLoadingState', { key: 'socialChat_loadingUsers', value: false })
            }
        },

        async selectChatUserFromQuery({ commit, dispatch, getters }, payload) {
            const carriedUser = getters.getSelectedUser
            commit('resetChats')

            const targetUserId = Number(payload?.id) || null
            const targetUserName = payload?.name ? String(payload.name) : ''

            if (!targetUserId || targetUserId === getters.getId) return

            const userFromLoadedList = (getters.getUsers || []).find((user) => user.id === targetUserId)

            if (userFromLoadedList) {
                await selectUserAndOpenChat({ commit, dispatch }, userFromLoadedList)
                return
            }

            if (targetUserName) {
                const usersSnapshot = [...(getters.getUsers || [])]

                await dispatch('getChatUsers', { search: targetUserName, page: 1, perPage: 10 })

                const userFromSearch = (getters.getUsers || []).find((user) => user.id === targetUserId)
                commit('setUsers', usersSnapshot)

                if (userFromSearch) {
                    commit('upsertChatUser', userFromSearch)
                    await selectUserAndOpenChat({ commit, dispatch }, userFromSearch)
                    return
                }
            }

            if (carriedUser?.id === targetUserId) {
                commit('setSelectedUser', carriedUser)
                commit('setSelectedChat', null)
            }
        },

        async createMessage({ commit, dispatch }, payload) {
            try {
                const response = await createChatMessage(payload)
                const chatId = payload.chat_id || payload.chatId

                commit('addMessageToChat', { chatId: chatId, message: response.data })
                commit('moveUserToTopAndUpdateLastMessage', { chatId: chatId, message: response.data })
            } catch (error) {
                dispatch('toastfy/activeErrorNotification', { text: error })
            }
        },

        async createChat({ commit, dispatch }, payload) {
            try {
                const response = await createSocialChat(payload)
                const newChat = response.data

                commit('setChatMessages', { chatId: newChat.id, messages: [] })
                commit('setSelectedChat', newChat)

                if (payload.user_id) {
                    commit('addChatToUser', {
                        userId: payload.user_id,
                        chat: {
                            id: newChat.id,
                            type: newChat.type,
                            name: newChat.name || null
                        }
                    });
                    commit('setUserPrivateChat', { userId: payload.user_id, chat: newChat });
                }

                return response
            } catch (error) {
                dispatch('toastfy/activeErrorNotification', { text: error })
            }
        },

        async deleteMessage({ commit, dispatch }, payload) {
            try {
                await deleteChatMessage(payload.messageId)
                commit('removeMessageFromChat', {
                    chatId: payload.chatId,
                    messageId: payload.messageId
                })
            } catch (error) {
                dispatch('toastfy/activeErrorNotification', { text: error })
            }
        },

        async updateMessage({ commit, dispatch }, payload) {
            try {
                const response = await updateChatMessage(payload.messageId, payload.data)
                commit('updateMessageInChat', {
                    chatId: payload.chatId,
                    messageId: payload.messageId,
                    updatedMessage: response.data
                })
            } catch (error) {
                dispatch('toastfy/activeErrorNotification', { text: error })
            }
        },

        processGlobalIncomingMessage({ commit, state, getters, dispatch }, payload) {
            console.log('processGlobalIncomingMessage', payload)
            const message = payload.message
            const chatId = message?.chat_id
            const currentUserId = getters.getId

            if (message?.sender?.id === currentUserId) return

            if (chatId && state.chats[chatId]) {
                commit('addMessageToChat', { chatId, message })
            }

            if (message?.sender?.id) {
                commit('setUserPrivateChatFromEvent', { senderId: message.sender.id, message })
            }

            if (chatId && message) {
                commit('moveUserToTopAndUpdateLastMessage', { chatId, message })
            }

            const isViewingThisChat = state.selectedChat?.id === chatId
            if (isViewingThisChat) {
                dispatch('viewMessage', { chatId, messageId: message.id })
                return
            }

            dispatch('toastfy/activeInfoNotification', { text: `Nova mensagem recebida` })
            playNotificationSound()
        },

        viewLastChatMessage({ state, getters, dispatch }, { chatId }) {
            const messages = state.chats[chatId]
            const lastMessage = messages?.[messages.length - 1]
            if (!lastMessage || lastMessage.sender?.id === getters.getId) return

            const alreadyViewed = lastMessage.last_viewed_by?.some(v => v.id === getters.getId)

            if (!alreadyViewed) {
                dispatch('viewMessage', { chatId, messageId: lastMessage.id })
            }
        },

        async viewMessage({ commit, getters }, { chatId, messageId }) {
            try {
                await viewChatMessage(messageId)
                commit('markLastMessageAsViewed', {
                    chatId,
                    viewer: { id: getters.getId, name: getters.getName }
                })
            } catch {}
        },

        processGlobalEditedMessage({ commit, state, getters }, payload) {
            const message = payload.message
            const chatId = message?.chat_id
            const currentUserId = getters.getId

            if (message?.sender?.id === currentUserId) return

            if (chatId && state.chats[chatId]) {
                commit('updateMessageInChat', { chatId, messageId: message.id, updatedMessage: message })
            }

            if (chatId && message) {
                commit('updateLastMessageIfMatch', { chatId, message })
            }
        },

        processGlobalDeletedMessage({ commit, state }, payload) {
            const { id: messageId, chatId } = payload

            if (chatId && state.chats[chatId]) {
                commit('removeMessageFromChat', { chatId, messageId })
            }

            commit('updateLastMessageAfterDelete', { chatId, deletedMessageId: messageId })
        },

        initGlobalChatNotificationsChannel(_, { userId }) {
            const channelEvents = [
                { eventName: '.message.sent', eventAction: 'processGlobalIncomingMessage' },
                { eventName: '.message.edited', eventAction: 'processGlobalEditedMessage' },
                { eventName: '.message.deleted', eventAction: 'processGlobalDeletedMessage' },
            ]

            connectToGlobalChatNotificationsChannel(userId, channelEvents)
        }
    }
}
